import type { Participacao } from '@/types/evento-publico';

// Regras dos "lotes" de ingresso.
// Só vale para eventos que vendem ingresso E cujos nomes de modalidade têm
// "lote" (sem diferenciar maiúsculas). Nos outros, nada muda.

export function nomeTemLote(nome: string | null | undefined): boolean {
  return !!nome && nome.toLowerCase().includes('lote');
}

export function agruparPorModalidade(participacoes: Participacao[]): Participacao[][] {
  const porModalidade = new Map<number, Participacao[]>();
  participacoes.forEach((p) => {
    const lista = porModalidade.get(p.modalidade_id) || [];
    lista.push(p);
    porModalidade.set(p.modalidade_id, lista);
  });
  return Array.from(porModalidade.values()).sort(
    (a, b) =>
      (a[0].ordem_modalidade ?? 0) - (b[0].ordem_modalidade ?? 0) ||
      a[0].modalidade_id - b[0].modalidade_id,
  );
}

function esgotado(p: Participacao): boolean {
  if (typeof p.vagas === 'number' && p.vagas > 0) {
    const restantes = p.restantes ?? p.vagas - (p.ocupadas ?? 0);
    if (restantes <= 0) return true;
  }
  return false;
}

// À venda agora: dentro do período e com vaga.
export function aVendaAgora(p: Participacao, agora: Date = new Date()): boolean {
  if (p.situacao !== 'abertas') return false;
  if (p.inscricoes_abrem_em && new Date(p.inscricoes_abrem_em) > agora) return false;
  if (p.inscricoes_fecham_em && new Date(p.inscricoes_fecham_em) <= agora) return false;
  return !esgotado(p);
}

export interface ProximoLote {
  nome: string;
  valorCentavos: number | null;
  valorSituacao: string;
  abreEm: string;
}

export interface ResultadoLotes {
  grupos: Participacao[][];
  proximo: ProximoLote | null;
}

// Mostra só o primeiro lote à venda (na ordem) e esconde os seguintes.
// Se nenhum nome tiver "lote", devolve tudo como está.
export function filtrarLotes(
  grupos: Participacao[][],
  vendeIngressos: boolean,
  agora: Date = new Date(),
): ResultadoLotes {
  if (!vendeIngressos) return { grupos, proximo: null };

  const todas = grupos.flat();
  if (todas.length === 0) return { grupos, proximo: null };
  if (!todas.some((p) => nomeTemLote(p.modalidade))) return { grupos, proximo: null };

  const ordenados = agruparPorModalidade(todas);
  const lotes = ordenados.filter((g) => nomeTemLote(g[0].modalidade));
  const outros = ordenados.filter(
    (g) => !nomeTemLote(g[0].modalidade) && g.some((p) => aVendaAgora(p, agora)),
  );

  const primeiro = lotes.findIndex((g) => g.some((p) => aVendaAgora(p, agora)));

  const visiveis: Participacao[][] = [...outros];
  let proximo: ProximoLote | null = null;

  if (primeiro >= 0) {
    visiveis.push(lotes[primeiro]);

    const seguinte = lotes[primeiro + 1];
    if (seguinte && !seguinte.some((p) => aVendaAgora(p, agora))) {
      const abertura = seguinte
        .map((p) => p.inscricoes_abrem_em)
        .filter((d): d is string => !!d)
        .sort()[0];
      if (abertura && new Date(abertura) > agora) {
        proximo = {
          nome: seguinte[0].modalidade,
          valorCentavos: seguinte[0].valor_centavos,
          valorSituacao: seguinte[0].valor_situacao,
          abreEm: abertura,
        };
      }
    }
  }

  visiveis.sort(
    (a, b) =>
      (a[0].ordem_modalidade ?? 0) - (b[0].ordem_modalidade ?? 0) ||
      a[0].modalidade_id - b[0].modalidade_id,
  );

  return { grupos: visiveis, proximo };
}